import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Toaster } from "@/components/ui/sonner";
import Login from "@/pages/Login";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import TaskWorkspacePage from "@/pages/TaskWorkspacePage";
import MemberProfilePage from "@/pages/MemberProfilePage";
import HQ from "@/pages/HQ";
import EntityWorkspace from "@/pages/EntityWorkspace";
import TasksPage from "@/pages/TasksPage";
import CalendarPage from "@/pages/CalendarPage";
import ContactsPage from "@/pages/ContactsPage";
import ContactDetail from "@/pages/ContactDetail";
import AIAssistantPage from "@/pages/AIAssistantPage";
import SearchPage from "@/pages/SearchPage";
import ChangePassword from "@/pages/ChangePassword";
import TemplatesPage from "@/pages/TemplatesPage";
import DigestPage from "@/pages/DigestPage";
import TeamPage from "@/pages/TeamPage";
import AcceptInvitePage from "@/pages/AcceptInvitePage";
import AccountPage from "@/pages/AccountPage";
import AuditPage from "@/pages/AuditPage";
import AttentionPage from "@/pages/AttentionPage";
import InboxPage from "@/pages/InboxPage";
import IntakePage from "@/pages/IntakePage";
import SignalsPage from "@/pages/SignalsPage";
import OrganizationsPage from "@/pages/OrganizationsPage";
import OrganizationProfilePage from "@/pages/OrganizationProfilePage";
import AskHQPage from "@/pages/AskHQPage";
import OpportunitiesPage from "@/pages/OpportunitiesPage";
import AppShell from "@/components/AppShell";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="h-screen flex items-center justify-center bg-[#0B0E14] text-white/60">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return <AppShell>{children}</AppShell>;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/" element={<Protected><HQ /></Protected>} />
          <Route path="/entity/:id" element={<Protected><EntityWorkspace /></Protected>} />
          <Route path="/tasks" element={<Protected><TasksPage /></Protected>} />
          <Route path="/task/:id" element={<Protected><TaskWorkspacePage /></Protected>} />
          <Route path="/member/:uid" element={<Protected><MemberProfilePage /></Protected>} />
          <Route path="/calendar" element={<Protected><CalendarPage /></Protected>} />
          <Route path="/contacts" element={<Protected><ContactsPage /></Protected>} />
          <Route path="/contacts/:id" element={<Protected><ContactDetail /></Protected>} />
          <Route path="/assistant" element={<Protected><AIAssistantPage /></Protected>} />
          <Route path="/ai" element={<Navigate to="/assistant" replace />} />
          <Route path="/search" element={<Protected><SearchPage /></Protected>} />
          <Route path="/templates" element={<Protected><TemplatesPage /></Protected>} />
          <Route path="/digest" element={<Protected><DigestPage /></Protected>} />
          <Route path="/team" element={<Protected><TeamPage /></Protected>} />
          <Route path="/account" element={<Protected><AccountPage /></Protected>} />
          <Route path="/audit" element={<Protected><AuditPage /></Protected>} />
          <Route path="/attention" element={<Protected><AttentionPage /></Protected>} />
          <Route path="/inbox" element={<Protected><InboxPage /></Protected>} />
          <Route path="/inbox/oauth/callback" element={<Protected><InboxPage /></Protected>} />
          <Route path="/intake" element={<Protected><IntakePage /></Protected>} />
          <Route path="/signals" element={<Protected><SignalsPage /></Protected>} />
          <Route path="/ask" element={<Protected><AskHQPage /></Protected>} />
          <Route path="/opportunities" element={<Protected><OpportunitiesPage /></Protected>} />
          <Route path="/organizations" element={<Protected><OrganizationsPage /></Protected>} />
          <Route path="/organizations/:id" element={<Protected><OrganizationProfilePage /></Protected>} />
          <Route path="/accept-invite" element={<AcceptInvitePage />} />
          <Route path="/settings/password" element={<Protected><ChangePassword /></Protected>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}
